// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Card } from '@/components/card';
import { PageContent } from '@/components/page-content';
import {
  AddCircleOutlineRounded,
  AddToPhotosOutlined,
  ChecklistRtlOutlined,
  DifferenceOutlined,
  DoNotDisturb,
  DriveFileMoveOutlined,
  GppGoodOutlined,
  InsightsOutlined,
  Inventory2Outlined,
  LocalShippingOutlined,
  MoveDownOutlined,
  NorthEastRounded,
  OfflineBoltOutlined,
  PriceChangeOutlined,
  RefreshRounded,
  ReplayRounded,
  RequestQuoteOutlined,
  SouthEastRounded,
  SsidChartOutlined,
  TimerOutlined,
  TrendingDownOutlined,
  TrendingUpOutlined,
  ViewTimelineOutlined,
  WaterfallChartOutlined,
} from '@/constants/hardfin-standard-icons';
import { ReportLink } from '@/report/components/report-link';
import { Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_app/reports/')({
  component: ReportsRoute,
});

/**
 * Renders the main reports page, organizing various reports into categories.
 * Displays reports related to assets, projects, and financials in a multi-column layout.
 */
function ReportsRoute() {
  return (
    <PageContent>
      <Stack direction="row" spacing={4}>
        {/* Asset reports column */}
        <Stack flex={1} spacing={2}>
          <Typography variant="h6" paddingLeft={1}>
            Asset reports
          </Typography>
          <Stack spacing={3}>
            <Card title="Logistics activity">
              <ReportLink
                to="/reports/inventory"
                icon={<Inventory2Outlined />}
                title="Inventory asset report"
                subtitle={
                  <span>
                    Understand <b>inventory unit volume</b> trends over time by{' '}
                    <b>product</b> and <b>location</b>
                  </span>
                }
              />
              <ReportLink
                to="/reports/transit"
                icon={<LocalShippingOutlined />}
                title="Transit asset report"
                subtitle={
                  <span>
                    Understand <b>unit volumes in transit</b> over time by{' '}
                    <b>product</b>
                  </span>
                }
              />
              <ReportLink
                to="/reports/field"
                icon={<OfflineBoltOutlined />}
                title="Field asset report"
                subtitle={
                  <span>
                    Understand <b>field unit volume</b> trends over time by{' '}
                    <b>product</b> and <b>location</b>
                  </span>
                }
              />
              <ReportLink
                to="/reports/fulfillment"
                icon={<ChecklistRtlOutlined />}
                title="Fulfillment report"
                subtitle={
                  <span>
                    Find <b>open SKU demand</b> by project across{' '}
                    <b>fulfillment coverage</b>
                  </span>
                }
              />
            </Card>
            <Card chip="Coming soon" title="Operational performance">
              <ReportLink
                isDisabled
                icon={<DifferenceOutlined />}
                title="Expected vs actual transit"
                subtitle={
                  <span>
                    Measure <b>actual</b> logistics performance against{' '}
                    <b>plan</b>
                  </span>
                }
              />
              <ReportLink
                isDisabled
                icon={<TimerOutlined />}
                title="Average deployment times"
                subtitle={
                  <span>
                    Measure your <b>typical</b> asset operations journey and
                    identify <b>outliers</b>
                  </span>
                }
              />
              <ReportLink
                isDisabled
                icon={<GppGoodOutlined />}
                title="Status report"
                subtitle={
                  <span>
                    Measure your <b>operational status</b> for normal and error
                    assets over time
                  </span>
                }
              />
            </Card>

            <Card chip="Coming soon" title="Advanced planning">
              <ReportLink
                isDisabled
                icon={<InsightsOutlined />}
                title="Demand forecast"
                subtitle={
                  <span>
                    Analyze upcoming <b>demand</b> and <b>return</b> signals to
                    understand purchasing needs
                  </span>
                }
              />
              <ReportLink
                isDisabled
                icon={<WaterfallChartOutlined />}
                title="Commercial utilization"
                subtitle={
                  <span>
                    Analyze project <b>assignments</b> compared with{' '}
                    <b>needs</b> to understand usage signals
                  </span>
                }
              />
            </Card>
          </Stack>
        </Stack>

        {/* Project reports column */}
        <Stack flex={1} spacing={2}>
          <Typography variant="h6" paddingLeft={1}>
            Project reports
          </Typography>
          <Stack spacing={3}>
            <Card title="Commercial history">
              <ReportLink
                to="/reports/active-projects"
                icon={<AddToPhotosOutlined />}
                title="Active projects report"
                subtitle={
                  <span>
                    Understand <b>active</b> projects and associated commercial
                    activity over time
                  </span>
                }
              />
              <ReportLink
                to="/reports/upcoming-projects"
                icon={<DriveFileMoveOutlined />}
                title="Upcoming projects report"
                subtitle={
                  <span>
                    Understand <b>upcoming</b> projects and associated
                    commercial activity over time
                  </span>
                }
              />
              <ReportLink
                to="/reports/past-projects"
                icon={<MoveDownOutlined />}
                title="Past projects report"
                subtitle={
                  <span>
                    Understand <b>past</b> projects and associated commercial
                    activity over time
                  </span>
                }
              />
            </Card>
            <Card title="Upcoming project starts">
              <ReportLink
                to="/reports/upcoming-project-starts"
                search={{ classifications: ['renewal'] }}
                icon={<ReplayRounded />}
                title="Upcoming renewals"
                subtitle={
                  <span>
                    Upcoming project starts that are <b>renewals</b> of a
                    previous project
                  </span>
                }
              />
              <ReportLink
                to="/reports/upcoming-project-starts"
                search={{ classifications: ['expansion'] }}
                icon={<NorthEastRounded />}
                title="Upcoming expansion"
                subtitle={
                  <span>
                    Upcoming project starts that are <b>expansion</b> for an
                    existing customer
                  </span>
                }
              />
              <ReportLink
                to="/reports/upcoming-project-starts"
                search={{ classifications: ['new-business'] }}
                icon={<AddCircleOutlineRounded />}
                title="Upcoming new business"
                subtitle={
                  <span>
                    Upcoming project starts that are <b>new business</b> for a
                    new customer
                  </span>
                }
              />
            </Card>
            <Card title="Upcoming project completions">
              <ReportLink
                to="/reports/upcoming-project-completions"
                search={{ classifications: ['renewed'] }}
                icon={<RefreshRounded />}
                title="Upcoming renewed"
                subtitle={
                  <span>
                    Upcoming project completion that has been <b>renewed</b>{' '}
                    into a new project
                  </span>
                }
              />
              <ReportLink
                to="/reports/upcoming-project-completions"
                search={{ classifications: ['contraction'] }}
                icon={<SouthEastRounded />}
                title="Upcoming contraction"
                subtitle={
                  <span>
                    Upcoming project completion that will be <b>contraction</b>{' '}
                    for an existing customer
                  </span>
                }
              />
              <ReportLink
                to="/reports/upcoming-project-completions"
                search={{ classifications: ['churn'] }}
                icon={<DoNotDisturb />}
                title="Upcoming churn"
                subtitle={
                  <span>
                    Upcoming project completion that will be <b>churn</b> for an
                    existing customer
                  </span>
                }
              />
            </Card>
            <Card chip="Coming soon" title="Utilization metrics">
              <ReportLink
                isDisabled
                icon={<SsidChartOutlined />}
                title="Project capacity report"
                subtitle={
                  <span>
                    Measure project <b>planned</b> vs <b>actual</b> asset
                    assignments to compare utilization over time
                  </span>
                }
              />
              <ReportLink
                isDisabled
                icon={<ViewTimelineOutlined />}
                title="Project timelines"
                subtitle={
                  <span>
                    Measure project <b>planned</b> and <b>actual</b> timelines
                    to understand project delivery
                  </span>
                }
              />
            </Card>
            <Card title="Commercial trajectory" chip="Coming soon">
              <ReportLink
                isDisabled
                icon={<TrendingUpOutlined />}
                title="Project renewal and expansion"
                subtitle={
                  <span>
                    Analyze project <b>renewals</b> and <b>expansion</b>{' '}
                    business over time
                  </span>
                }
              />
              <ReportLink
                isDisabled
                icon={<TrendingDownOutlined />}
                title="Project churn and contraction"
                subtitle={
                  <span>
                    Analyze project <b>churn</b> and <b>contraction</b> business
                    over time
                  </span>
                }
              />
            </Card>
          </Stack>
        </Stack>

        {/* Financial reports column */}
        <Stack flex={1} spacing={2}>
          <Typography variant="h6" paddingLeft={1}>
            Financial reports
          </Typography>
          <Stack spacing={3}>
            <Card title="Project financials" chip="Coming soon">
              <ReportLink
                isDisabled
                icon={<RequestQuoteOutlined />}
                title="Project revenue reporting"
                subtitle={
                  <span>
                    Analyze project <b>revenues</b> to understand business{' '}
                    <b>top-line</b> progress over time
                  </span>
                }
              />
              <ReportLink
                isDisabled
                icon={<PriceChangeOutlined />}
                title="Project P&L reporting"
                subtitle={
                  <span>
                    Analyze project <b>revenue and cost</b> to understand
                    business <b>gross margin</b> progress over time
                  </span>
                }
              />
            </Card>
          </Stack>
        </Stack>
      </Stack>
    </PageContent>
  );
}
